import { Injectable } from "@angular/core";
import {
    AuthData,
    AuthToken,
    AuthTokenResponse,
    RefreshToken,
    RefreshTokenRequest,
    RefreshTokenResponse
} from '@pcp/core/interfaces';
import { Endpoint } from '@pcp/http';
import { Observable } from 'rxjs';
import { map } from "rxjs/operators";
import { TokensAndDecodedToken } from "../interfaces/token-error";
import { VdoSession } from "../interfaces/vdo-session";
import { PcpTokenDecoderService } from "./pcp-token-decoder.service";


@Injectable({
    providedIn: 'root',
})
export class AuthApiService {
    constructor(
        private readonly endpoint: Endpoint,
        private readonly pcpTokenDecoder: PcpTokenDecoderService,
    ) {
    }

    public authWithSerials(serials: string[]): Observable<TokensAndDecodedToken> {
        return this.endpoint.post<AuthTokenResponse, AuthData>('/api/auth', { serials }, 'json').pipe(
            map(response => this.pcpTokenDecoder.decodeAndValidateToken(response, true)),
        );
    }

    public authWithCredentials(username: string, password: string): Observable<TokensAndDecodedToken> {
        return this.endpoint.post<AuthTokenResponse, AuthData>('/api/auth', { username, password }, 'json').pipe(
            map(response => this.pcpTokenDecoder.decodeAndValidateToken(response, true)),
        );
    }

    public authAsStudent(username: string, password: string): Observable<TokensAndDecodedToken> {
        return this.endpoint.post<AuthTokenResponse, AuthData>('/api/auth', {
            username,
            password,
            clientType: "Student"
        }, 'json').pipe(
            map(response => this.pcpTokenDecoder.decodeAndValidateToken(response, true)),
        );
    }

    public authAsStudentWithRegistrationCodeAndEnetToken(
        studentRegistrationCode: string,
        enetToken: string
    ): Observable<TokensAndDecodedToken> {
        return this.endpoint.post<AuthTokenResponse, AuthData>('/api/auth', {
            studentRegistrationCode,
            enetToken,
            clientType: "Student"
        }, 'json').pipe(
            map(response => this.pcpTokenDecoder.decodeAndValidateToken(response, true)),
        );
    }

    public authWithLessonCode(lessonCode: string): Observable<TokensAndDecodedToken> {
        return this.endpoint.post<AuthTokenResponse, AuthData>('/api/auth', { lessonCode }, 'json').pipe(
            map(response => this.pcpTokenDecoder.decodeAndValidateToken(response, true)),
        );
    }

    /** 503 where `VdoConfig` is incomplete - the per-environment kill switch. */
    public getVdoSession(): Observable<VdoSession> {
        return this.endpoint.get<VdoSession>('/api/auth/vdo');
    }

    public refreshToken(accessToken: AuthToken, refreshToken: RefreshToken): Observable<TokensAndDecodedToken> {
        return this.endpoint.post<RefreshTokenResponse, RefreshTokenRequest>('/api/auth/refresh', {
            accessToken,
            refreshToken
        }, 'json').pipe(
            map(response => this.pcpTokenDecoder.decodeAndValidateToken(response, true)),
        );
    }
}
